'use strict';

import GoogleRecaptcha from 'Extensions/google-recaptcha';

/**
 * Helper class that is used with a form element that is wrapped in a `template` element. For
 * more information on `template` elements please visit https://developer.mozilla.org/en-US/docs/Web/HTML/Element/template
 *
 * Examples:
 * ReadyTheme Content Sections:
 * request_appointment_form
 * drop_hint
 * product_details_form
 *
 * Dependancies:
 * jQuery
 * magnificPopup
 * GoogleRecaptcha
 *
 * @param  {HTMLElement} template
 * @param  {HTMLElement} trigger
 * @return {FormModal}
 */
export default class FormModal {
	constructor ({
		template = null,
		trigger = null
	} = {}) {
		// Defaults
		this.templateElement = null;

		// Properties
		this.template = template;
		this.trigger = trigger;

		this.onInit();
	}

	// Getters & Setters

	/**
	 * Returns the first element within the `template` element
	 */
	getTemplateElement () {
		return this.templateElement;
	}

	/**
	 * @param  {HTMLElement} template_element
	 */
	setTemplateElement (templateElement) {
		this.templateElement = templateElement;
	}

	/**
	 * Entry function within the constructor
	 */
	onInit () {
		if (this.trigger === null || this.template === null) {
			return;
		}

		// Get contents of the template
		const templateElement = document.documentElement.classList.contains('ie')
			? this.template.firstElementChild
			: this.template.content.firstElementChild;

		this.setTemplateElement(templateElement);

		// Event: Bind Click
		this.trigger.addEventListener('click', this._onClick.bind(this));

		// Initiate Popup
		$(this.trigger).magnificPopup({
			callbacks: {
				open: function () {
					// Get & set Google reCaptcha
					new GoogleRecaptcha().get(templateElement);
				}
			},
			items: {
				src: $(templateElement),
				type: 'inline'
			}
		});
	}

	// Events

	/**
	 * Internal `click` event that is used to append the internal template elements to the DOM
	 * @param  {Event<Object>} event
	 */
	_onClick (event) {
		event.currentTarget.removeEventListener('click', this.onClick);
		document.body.appendChild(this.template);
		if (typeof this.onTemplateAddedCallback === 'function') {
			this.onTemplateAddedCallback();
		}
	}

	/**
	 * On `submit` event callback which allows the user to determine how the form submission
	 * should be handled
	 * @param  {Function} callback
	 */
	onSubmit (callback = null) {
		if (callback === null) {
			return;
		}

		if (this.getTemplateElement() === null) {
			return;
		}

		const form = this.getTemplateElement().querySelector('form');

		form.removeEventListener('submit', callback);
		form.addEventListener('submit', callback);
	}

	onTemplateAdded (callback) {
		this.onTemplateAddedCallback = callback;
	}
}
